/**
 * Input formatting helpers for the Personal Data Sheet.
 *
 * Kept deliberately permissive: these run on every keystroke, so anything we
 * do not recognise has to pass through untouched or the client cannot finish
 * typing it. International numbers in particular must survive.
 */

/**
 * Format a US phone number as the client types.
 *
 * 10 digits  -> (555) 555-5555
 * 11 digits starting with 1 -> 1 (555) 555-5555
 * Anything else (partial entry, international, extensions) is returned as
 * typed so the field never fights the client.
 */
export function formatPhone(raw) {
  const value = String(raw ?? '');
  // A "+" means the client is typing an international number - hands off.
  if (value.includes('+')) return value;

  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return value;

  const national = digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
  if (national.length > 10) return value;

  const prefix = digits.length === 11 ? '1 ' : '';
  const area = national.slice(0, 3);
  const exchange = national.slice(3, 6);
  const line = national.slice(6, 10);

  if (national.length <= 3) return `${prefix}(${area}`;
  if (national.length <= 6) return `${prefix}(${area}) ${exchange}`;
  return `${prefix}(${area}) ${exchange}-${line}`;
}

/** Today as yyyy-mm-dd, for `max` on date inputs. */
export function todayIso() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Earliest date we accept for a date of birth. */
export const MIN_DOB = '1900-01-01';
